import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";

import { ProvidersAndConfigs } from "@components/ProvidersAndConfigs";
import { PageProgress } from "@components/PageProgress";
import { PageGuard } from "@components/PageGuard";
import { ErrorBoundaryRoot } from "@components/ErrorBoundary/Root";
import { PageLayout } from "@components/PageLayout";
import { PageLayoutProtected } from "@components/PageLayoutProtected";
import { featureFlags } from "./settings/flags";
import { isTrainingTabEnabled } from "./settings/whiteLabel";

const { enableUsersCrud } = featureFlags;

const hasUsersCrud = enableUsersCrud;

const protectedRoutes = [
  {
    path: "/servicos",
    lazy: () =>
      featureFlags.enableServicesPageV2
        ? import("@pages/ServicesV2")
        : import("@pages/Services"),
  },
  {
    path: "/termos-de-uso",
    lazy: () => import("@pages/Terms"),
  },
  {
    path: "/politica-de-privacidade",
    lazy: () => import("@pages/PrivacyPolicy"),
  },
  {
    path: "/contratos/recalculo-margem-livre/:token_contrato",
    lazy: () => import("@pages/Contracts/FreeMarginRecalculation"),
  },
  {
    path: "/contratos/ajuste-taxa-port-refin/:token_contrato",
    lazy: () => import("@pages/Contracts/PortabilityRefinRateAdjustment"),
  },
  {
    path: "/contratos/:token_contrato?",
    lazy: () => import("@pages/Contracts"),
  },
  {
    path: "/inss/margem-livre",
    lazy: () => import("@pages/journeys/InssFreeMargin"),
  },
  {
    path: "/inss/portabilidade",
    lazy: () => import("@pages/journeys/InssPortability"),
  },
  {
    path: "/inss/portabilidade-refin",
    lazy: () => import("@pages/journeys/InssPortabilityRefin"),
  },
  {
    path: "/inss/refinanciamento",
    lazy: () =>
      featureFlags.enableInssRefinancingCef
        ? import("@pages/journeys/InssRefinancingCef")
        : import("@pages/journeys/InssRefinancing"),
  },
  {
    path: "/cartao/cartao-beneficio",
    lazy: () => import("@pages/journeys/BenefitCard"),
  },
  {
    path: "/saque-complementar",
    lazy: () => import("@pages/journeys/BenefitCardWithdrawal"),
  },
  {
    path: "/portabilidade-ataque",
    lazy: () => import("@pages/PortabilityAttacks"),
  },
  {
    path: "/portabilidade-ataque/contratos",
    lazy: () => import("@pages/PortabilityAttacks"),
  },
  {
    path: "/portabilidade-ataque/acompanhamento",
    lazy: () => import("@pages/FollowRetention"),
  },
  {
    path: "/cartao/agregacao-margem",
    lazy: () => import("@pages/journeys/AggregationMarginCard"),
  },
  {
    path: "/inss/multiproduto",
    lazy: () => import("@pages/journeys/Multiproduct"),
  },
];

if (isTrainingTabEnabled()) {
  protectedRoutes.push({
    path: "/treinamentos",
    lazy: () => import("@pages/Trainings"),
  });
}

if (
  featureFlags.enableDoNotDisturbTab ||
  featureFlags.enableDoNotDisturbTabCef
) {
  protectedRoutes.push({
    path: "/nao-me-perturbe",
    lazy: () => import("@pages/DoNotDisturb"),
  });
}

if (hasUsersCrud) {
  protectedRoutes.push(
    {
      path: "/usuarios",
      lazy: () => import("@pages/Users"),
    },
    {
      path: "/usuarios/novo",
      lazy: () => import("@pages/Users/NewUser"),
    },
  );
}

if (featureFlags.enableProductReceba) {
  protectedRoutes.push({
    path: "/receba",
    lazy: () => import("@pages/journeys/Receba"),
  });
}

if (
  featureFlags.enablePrivateConsigLoan ||
  featureFlags.enablePrivateConsigCef
) {
  protectedRoutes.push({
    path: "/consignado-privado",
    lazy: () =>
      featureFlags.enablePrivateConsigCef
        ? import("@pages/journeys/PrivateConsigCef")
        : import("@pages/journeys/PrivateConsig"),
  });
}

if (featureFlags.enablePrivateConsigV2) {
  protectedRoutes.push({
    path: "/v2/consignado-privado",
    lazy: () => import("@pages/journeys/PrivateConsigV2"),
  });
}

if (featureFlags.enablePublicConsig) {
  protectedRoutes.push({
    path: "/consignado-publico",
    lazy: () => import("@pages/journeys/PublicConsig"),
  });
}

if (featureFlags.enableMarginIncrease) {
  protectedRoutes.push({
    path: "/inss/aumento-margem",
    lazy: () => import("@pages/journeys/InssMarginIncrease"),
  });
}

if (featureFlags.enableBatchTypingMarginIncrease) {
  protectedRoutes.push({
    path: "/inss/aumento-margem-lote",
    lazy: () => import("@pages/journeys/InssMarginIncreaseBatch"),
  });
}

if (featureFlags.enableSupportMaterials) {
  protectedRoutes.push(
    {
      path: "/materiais-de-apoio",
      lazy: () => import("@pages/SupportMaterials/index"),
    },
    {
      path: "/materiais-de-apoio/visualizar-documento",
      lazy: () => import("@pages/SupportMaterials/VisualizeMaterial/index"),
    },
  );
}

export const router = createBrowserRouter(
  [
    {
      element: <ProvidersAndConfigs />,
      children: [
        {
          element: <PageProgress />,
          children: [
            {
              path: "",
              element: <PageGuard />,
              errorElement: <ErrorBoundaryRoot />,
              children: [
                {
                  element: <PageLayout />,
                  children: [
                    {
                      path: "/login",
                      lazy: () => import("@pages/auth/Login"),
                    },
                    {
                      path: "/esqueci-senha",
                      lazy: () => import("@pages/auth/ForgotPassword"),
                    },
                    {
                      path: "/nova-senha",
                      lazy: () => import("@pages/auth/ResetPassword"),
                    },
                    {
                      path: "/reset-password/:uid/:token",
                      lazy: () => import("@pages/auth/ResetPassword"),
                    },
                    {
                      path: "/validacao",
                      lazy: () => import("@pages/auth/Validation"),
                    },
                    {
                      path: "/selecao-corban",
                      lazy: () => import("@pages/CorbanSelect"),
                    },
                    {
                      path: "*",
                      lazy: () => import("@pages/fallbacks/NotFound"),
                    },
                    {
                      path: "/sem-acesso",
                      lazy: () => import("@pages/fallbacks/NotAllowed"),
                    },
                  ],
                },
                {
                  element: <PageLayoutProtected />,
                  children: protectedRoutes,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {},
  },
);
